import React from 'react';
import { Flex, Result } from 'antd';
import LastSectionCompletionModal from '../components/LastSectionCompletionModal';
import { store } from '../index';
import {
  showModal,
  setCurrentSectionId,
  updateSectionStatus,
} from '../store/actions/AssessmentActions';
import { TRIGGER_TIMER, SECTION_STATUS_ENDED, SECTION_STATUS_IN_PROGRESS } from '../const';

/**
 * Shows the modal for completing the last section
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} actions - Actions object
 * @param {string} sectionId - Section ID
 * @param {string} assessmentId - Assessment ID
 */
export const showLastSectionCompletionModal = (dispatch, actions, sectionId, assessmentId) => {
  dispatch(
    showModal({
      name: 'submit_last_section_confirm',
      type: 'confirm',
      title: <LastSectionCompletionModal />,
      content: (
        <p>
          You've reached the end of the test. Please click<strong> Submit </strong> to finish and
          submit your answers.
        </p>
      ),
      okText: 'Submit',
      cancelText: 'Go Back',
      onOk: async () => {
        await dispatch(actions.updateSectionStatus(sectionId, SECTION_STATUS_ENDED));
        await new Promise(resolve => setTimeout(resolve, 1000));
        dispatch(actions.submitAssessment(assessmentId, true));
      },
    }),
  );
};

/**
 * Shows the modal for submitting a section
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} onSubmitSection - Function to call when submitting the section
 */
export const showSubmitSectionConfirmModal = (dispatch, onSubmitSection) => {
  dispatch(
    showModal({
      name: 'submit_section_confirm',
      type: 'confirm',
      title: 'Submit Section',
      content: 'Submitting will lock this section and you’ll automatically move to the next',
      okText: 'Submit',
      cancelText: 'Go Back',
      onOk: () => {
        onSubmitSection();
      },
    }),
  );
};

/**
 * Updates the section route using history, sets the current section, and marks it as in progress
 * @param {Object} params - Parameters object
 * @param {Array} params.sections - Array of sections
 * @param {string} params.assessmentId - Assessment ID
 * @param {string} params.sectionId - Section ID
 * @param {Object} params.history - React Router history object
 * @param {Function} params.setCurrentSectionId - Function to set current section ID
 * @param {Function} params.handleSidePanelClick - Function to handle side panel click for problems
 * @param {Function} params.handleMcqSidePanelClick - Function to handle side panel click for MCQs
 */
export const updateSectionRoute = async ({
  sections,
  assessmentId,
  sectionId,
  history,
  handleSidePanelClick,
  handleMcqSidePanelClick,
  isPrevious = false,
}) => {
  const currentSection = sections.find(s => s.id === sectionId);
  if (currentSection && currentSection.status === SECTION_STATUS_IN_PROGRESS) {
    const type = currentSection.type;
    const questionId = isPrevious
      ? type === 'problems'
        ? currentSection.problems[currentSection.problems.length - 1]
        : currentSection.mcqs[currentSection.mcqs.length - 1]
      : type === 'problems'
      ? currentSection.problems[0]
      : currentSection.mcqs[0];
    if (questionId) {
      const route =
        type === 'problems'
          ? `/assessment/${assessmentId}/problem/${questionId}`
          : `/assessment/${assessmentId}/mcqs/${questionId}`;
      history.push(route);
      if (type === 'problems' && handleSidePanelClick) {
        handleSidePanelClick(questionId);
      } else if (type === 'mcqs' && handleMcqSidePanelClick) {
        handleMcqSidePanelClick();
      }
    } else {
      history.push(`/assessment/${assessmentId}/section/${sectionId}`);
    }
  } else {
    store.dispatch(updateSectionStatus(sectionId, SECTION_STATUS_IN_PROGRESS));
    history.push(`/assessment/${assessmentId}/section/${sectionId}`);
  }
  store.dispatch(setCurrentSectionId(sectionId));
};

/**
 * Switches to the next section in the assessment workflow
 * @param {Object} params - Parameters object
 * @param {Array} params.sections - Array of sections
 * @param {string} params.currentSectionId - Current section ID
 * @param {Object} params.history - React Router history object
 * @param {string} params.assessmentId - Assessment ID
 * @param {string} params.endBy - Trigger type ('button' or 'timer')
 * @param {Function} params.submitTest - Callback to show modal for last section
 */
export const switchToNextSection = async ({
  sections,
  currentSectionId,
  history,
  assessmentId,
  endBy,
  submitTest = () => {},
}) => {
  if (sections && sections.length > 0 && currentSectionId) {
    const currentSectionIndex = sections.findIndex(section => section.id === currentSectionId);
    const currentSection = sections[currentSectionIndex];
    if (currentSectionIndex === sections.length - 1) {
      // Last section
      if (endBy === TRIGGER_TIMER) {
        await store.dispatch(updateSectionStatus(currentSection.id, SECTION_STATUS_ENDED));
        await new Promise(resolve => setTimeout(resolve, 1000));
        submitTest(assessmentId, true);
      }
    } else if (currentSectionIndex !== -1 && currentSectionIndex < sections.length - 1) {
      const currentSection = sections[currentSectionIndex];
      // Mark current section as ended
      await store.dispatch(updateSectionStatus(currentSection.id, SECTION_STATUS_ENDED));
      const nextSection = sections[currentSectionIndex + 1];

      await updateSectionRoute({
        sections,
        assessmentId,
        sectionId: nextSection.id,
        history,
        handleSidePanelClick: null,
        handleMcqSidePanelClick: null,
      });
    }
  }
};
